import React, { useEffect, useState } from "react";
import { Box, TextField, Typography, InputAdornment } from "@mui/material";
import { ResourceLinks } from "../../../../constants";
import { useSelector } from "../../../../store";
import { floatOnly } from "../../../../utils";

export default function WebNumeracyArea({ question, onAnswer = () => {} }) {
    const questionData = question;
    const learnerState = useSelector((state) => state.learner || {});
    const data = learnerState?.details?.testLevel === "vfh" ? questionData?.vfh : questionData?.acsf4;
    const imagesResources = ResourceLinks.llnQuizImage;

    // Initial states for the text fields inside the image and for answer fields
    const initialTextFieldsState = data.textFieldPositions.reduce(
        (acc, field) => {
            acc[field.stateKey] = "";
            return acc;
        },
        {}
    );
    const [inputValues, setInputValues] = useState(initialTextFieldsState);

    const [totalArea, setTotalArea] = useState("");
    const [totalCost, setTotalCost] = useState("");

    // const [finalAnswer, setFinalAnswer] = useState({});

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        // setInputValues(initialTextFieldsState);
        setTotalArea("");
        setTotalCost("");
        // setFinalAnswer({});
    }, [question,
        // initialTextFieldsState,
    ]);

    // Function to build the full value object for logging
    const buildValueObject = (updatedKey = null, updatedValue = null) => {
        const updatedValues = {
            ...Object.keys(inputValues).reduce((acc, key) => {
                acc[key] = (parseFloat(
                    updatedKey === key ? updatedValue : inputValues[key]
                ) || "").toString();
                return acc;
            }, {}),
        };

        updatedValues["totalArea"] = (parseFloat(updatedKey === "totalArea" ? updatedValue : totalArea) || "").toString();
        updatedValues["totalCost"] = (parseFloat(updatedKey === "totalCost" ? updatedValue : totalCost) || "").toString();

        return updatedValues;
    };

    // Log the complete value object when any input is changed
    const logUpdate = (updatedKey, updatedValue) => {
        // setFinalAnswer(buildValueObject(updatedKey, updatedValue));
        checkCanProceed(buildValueObject(updatedKey, updatedValue));
    };

    // Handler for changing the text fields inside the image
    const handleInputChange = (key, e) => {
        if (floatOnly(e.target.value) || e.target.value === "") {
            const newValue = e.target.value;
            setInputValues((prev) => ({
                ...prev,
                [key]: newValue,
            }));
            
            logUpdate(key, newValue);
        }
    };

    // Handlers for total area and total cost fields
    const handleTotalAreaChange = (e) => {
        if (floatOnly(e.target.value) || e.target.value === "") {
            const newValue = e.target.value;
            setTotalArea(newValue);
            logUpdate("totalArea", newValue);
        }
    };

    const handleTotalCostChange = (e) => {
        if (floatOnly(e.target.value) || e.target.value === "") {
            const newValue = e.target.value;
            setTotalCost(newValue);
            logUpdate("totalCost", newValue);
        }
    };

    const checkCanProceed = (ans) => {
        const answerKeys = Object.keys(ans);

        let canProceed = false;
        answerKeys.map((keys) => {
            if (ans[keys] !== "" && ans[keys] !== null) {
                canProceed = true;
            }
            return null;
        });

        onAnswer({
            canProceed,
            answer: ans
        });
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
                backgroundColor: "white",
                padding: "20px",
                flexDirection: { xs: "column", sm: "row" },
            }}
        >
            {/* Image with text fields */}
            <Box
                sx={{
                    flex: "none",
                    width: "511px",
                    paddingRight: { sm: "20px" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                {/* Floor plan image */}
                <Box component="img"
                    src={`${imagesResources}${data.image}`}
                    alt="Floor Plan"
                    sx={{
                        width: "511px",
                        height: "auto",
                        borderRadius: "5px",
                    }}
                />

                {/* Positioned text fields on the image */}
                {data.textFieldPositions.map((field, index) => (
                    <Box
                        key={index}
                        sx={{
                            position: "absolute",
                            top: field.top,
                            left: field.left,
                            width: field.width,
                            height: "6%",
                            backgroundColor: "white",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <TextField
                            variant="standard"
                            type="text"
                            value={inputValues[field.stateKey]}
                            onChange={(e) =>
                                handleInputChange(field.stateKey, e)
                            }
                            InputProps={{
                                autoComplete: 'off',
                                disableUnderline: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {field.after}
                                    </InputAdornment>
                                ),
                                fontSize: "20px",
                            }}
                            sx={{
                                width: "100%",
                                textAlign: "center",
                                backgroundColor: "white",
                                borderBottom: "rgb(136, 136, 136)",
                                input: {
                                    padding: "4px",
                                    fontSize: "20px",
                                }
                            }}
                        />
                    </Box>
                ))}

                {/* Carpet price display */}
                <Box
                    sx={{
                        marginTop: "20px",
                        textAlign: "left",
                        width: "100%",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: "rgb(29, 98, 149)",
                            fontWeight: "bold",
                        }}
                    >
                        Carpet Price:
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: "24px",
                            fontWeight: "bold",
                        }}
                    >
                        {data.carpetPrice}
                    </Typography>
                </Box>
            </Box>

            {/* Answer fields on the right */}
            <Box
                sx={{
                    flex: "1",
                    maxWidth: { xs: "100%", sm: "30%" },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                {data.answerFields.map((field, index) => (
                    <Box
                        key={index}
                        sx={{
                            borderRadius: "5px",
                            borderBottom: "10px solid rgb(91, 105, 107)",
                            backgroundColor: "rgb(122, 140, 142)",
                            padding: "10px",
                            marginBottom: "20px",
                            width: { xs: "90%", sm: "100%", md: "210px" },
                            textAlign: "left",
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: "white",
                                marginBottom: "8px",
                                fontWeight: "bold",
                            }}
                        >
                            {field.label}
                        </Typography>
                        <TextField
                            variant="outlined"
                            type="text"
                            value={
                                field.stateKey === "totalArea"
                                    ? totalArea
                                    : totalCost
                            }
                            onChange={
                                field.stateKey === "totalArea"
                                    ? handleTotalAreaChange
                                    : handleTotalCostChange
                            }
                            InputProps={{
                                autoComplete: 'off',
                                endAdornment: field.unit === "m²" && (
                                    <InputAdornment position="end">
                                        {field.unit}
                                    </InputAdornment>
                                ),
                                startAdornment: field.unit === "$" && (
                                    <InputAdornment position="start">
                                        {field.unit}
                                    </InputAdornment>
                                ),
                                fontSize: "20px",
                            }}
                            sx={{
                                width: "100%",
                                backgroundColor: "white",
                                borderRadius: "5px",
                                input: {
                                    fontSize: "20px",
                                    padding: "12px 8px 12px 8px",
                                }
                            }}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
